import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { AlertTriangle, ShieldOff, ShieldCheck, Clock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const TemporaryTwoFADisable = ({ user }) => {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState({
    twofa_temp_disabled: false,
    disabled_until: null
  });
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    const token = localStorage.getItem('token');
    
    try {
      const response = await axios.get(
        `${API_URL}/api/superadmin/2fa-temp-status`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      setStatus(response.data);
    } catch (err) {
      console.error('Fehler beim Laden des 2FA-Status:', err);
    }
  };

  const handleToggle = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    
    try {
      const response = await axios.post(
        `${API_URL}/api/superadmin/toggle-2fa-temporary`,
        {},
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      setStatus({
        twofa_temp_disabled: response.data.twofa_temp_disabled,
        disabled_until: response.data.disabled_until
      });
      
      setShowConfirm(false);
      
      // Show success message
      alert(response.data.message);
    } catch (err) {
      alert('Fehler: ' + (err.response?.data?.detail || 'Unbekannter Fehler'));
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return '-';
    return new Date(isoString).toLocaleString('de-DE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Only show for superadmin
  if (user?.role !== 'superadmin') {
    return null;
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
      <div className="flex items-start gap-4">
        <div className={`p-3 rounded-lg ${
          status.twofa_temp_disabled 
            ? 'bg-orange-100 dark:bg-orange-900/20' 
            : 'bg-blue-100 dark:bg-blue-900/20'
        }`}>
          {status.twofa_temp_disabled ? (
            <ShieldOff className="w-6 h-6 text-orange-600" />
          ) : (
            <ShieldCheck className="w-6 h-6 text-blue-600" />
          )}
        </div>
        
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
            Temporäre 2FA-Deaktivierung
          </h3>
          
          <div className="mb-4">
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${
              status.twofa_temp_disabled
                ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
                : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
            }`}>
              {status.twofa_temp_disabled ? '⚠️ 2FA TEMPORÄR DEAKTIVIERT' : '✓ 2FA AKTIV'}
            </div>
            
            {status.twofa_temp_disabled && status.disabled_until && (
              <div className="mt-3 flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                <Clock className="w-4 h-4" />
                <span>Automatische Reaktivierung: {formatDate(status.disabled_until)}</span>
              </div>
            )}
          </div>

          {!status.twofa_temp_disabled && !showConfirm && (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-4 mb-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-yellow-900 dark:text-yellow-200 mb-2">
                    ⚠️ NUR FÜR ENTWICKLUNG / SUPPORT
                  </p>
                  <ul className="text-yellow-800 dark:text-yellow-300 space-y-1 list-disc list-inside">
                    <li>Dies ist ein <strong>Sicherheitsrisiko</strong></li>
                    <li>Nur für temporäres Testing verwenden</li>
                    <li>Automatische Reaktivierung nach <strong>24 Stunden</strong></li>
                    <li>Kann jederzeit manuell reaktiviert werden</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {showConfirm && !status.twofa_temp_disabled && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-4 mb-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-bold text-red-900 dark:text-red-200 mb-2">
                    ⚠️ SICHERHEITSWARNUNG
                  </p>
                  <p className="text-red-800 dark:text-red-300 mb-3">
                    Sie sind dabei, die 2-Faktor-Authentifizierung temporär zu deaktivieren. 
                    Dies reduziert die Sicherheit Ihres Accounts erheblich!
                  </p>
                  <p className="text-red-800 dark:text-red-300 font-medium">
                    Sind Sie sicher, dass Sie fortfahren möchten?
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="flex gap-3">
            {!showConfirm ? (
              <button
                onClick={() => {
                  if (status.twofa_temp_disabled) {
                    // Direct toggle if already disabled
                    handleToggle();
                  } else {
                    // Show confirmation first
                    setShowConfirm(true);
                  }
                }}
                disabled={loading}
                className={`px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50 ${
                  status.twofa_temp_disabled
                    ? 'bg-green-600 text-white hover:bg-green-700'
                    : 'bg-orange-600 text-white hover:bg-orange-700'
                }`}
              >
                {loading ? (
                  'Wird verarbeitet...'
                ) : status.twofa_temp_disabled ? (
                  '✓ 2FA Wieder Aktivieren'
                ) : (
                  '⚠️ 2FA Temporär Deaktivieren'
                )}
              </button>
            ) : (
              <>
                <button
                  onClick={handleToggle}
                  disabled={loading}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 disabled:opacity-50"
                >
                  {loading ? 'Wird deaktiviert...' : 'Ja, Deaktivieren'}
                </button>
                <button
                  onClick={() => setShowConfirm(false)}
                  disabled={loading}
                  className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-300 dark:hover:bg-gray-600"
                >
                  Abbrechen
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
